/**
 * The environment half of a feedback snapshot (SA-1304): read when a snapshot is taken, never
 * kept in the ring. Family and major version only from the user agent; whether a View-as is
 * active, never who; the deployment's enabled features, the same for everyone.
 */
import { useAuthStore } from '@/stores/authStore'
import { useFeatureStore } from '@/stores/featureStore'
import { useFeedbackContextStore } from '@/stores/feedbackContextStore'
import { cleanEnvironment, type FeedbackEvent } from './allowlist'

export type Browser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'
export type Platform = 'macos' | 'windows' | 'ios' | 'android' | 'linux' | 'other'

function major(ua: string, pattern: RegExp): number | undefined {
  const match = ua.match(pattern)
  return match ? Number(match[1]) : undefined
}

/** The browser family and its major version; order matters (Edge says Chrome, Chrome says Safari). */
export function parseBrowser(ua: string): { browser: Browser; browser_version?: number } {
  if (/Edg(e|A|iOS)?\//.test(ua)) return { browser: 'edge', browser_version: major(ua, /Edg(?:e|A|iOS)?\/(\d+)/) }
  if (/Firefox\/|FxiOS\//.test(ua)) return { browser: 'firefox', browser_version: major(ua, /(?:Firefox|FxiOS)\/(\d+)/) }
  if (/Chrome\/|CriOS\//.test(ua) && !/OPR\//.test(ua)) return { browser: 'chrome', browser_version: major(ua, /(?:Chrome|CriOS)\/(\d+)/) }
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return { browser: 'safari', browser_version: major(ua, /Version\/(\d+)/) }
  return { browser: 'other' }
}

export function parsePlatform(ua: string): Platform {
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macos'
  if (/Windows/.test(ua)) return 'windows'
  if (/Linux|X11/.test(ua)) return 'linux'
  return 'other'
}

export function appBuild(): string {
  return import.meta.env.VITE_APP_BUILD || 'dev'
}

export type FeedbackEnvironment = Record<string, unknown>

export function environmentSnapshot(): FeedbackEnvironment {
  const features = useFeatureStore.getState()
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  return cleanEnvironment({
    features: features.isLoaded ? [...features.features].sort() : [],
    view_as: useAuthStore.getState().impersonation != null,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    ...parseBrowser(ua),
    platform: parsePlatform(ua),
    build: appBuild(),
  })
}

export interface FeedbackSnapshot {
  environment: FeedbackEnvironment
  events: FeedbackEvent[]
}

/** The one thing the panel calls: the environment now and the ring's events, both cleaned. */
export function takeSnapshot(now: number = Date.now()): FeedbackSnapshot {
  return { environment: environmentSnapshot(), events: useFeedbackContextStore.getState().snapshotEvents(now) }
}
